import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, MessageSquare, X, Send, ArrowUpRight } from "lucide-react";
import { fetchAskUsage, askCartIQ, fetchMacroShiftWelcome } from "@/lib/api";
import { tapFeedback } from "@/lib/utils";
import NutritionOverview from "@/components/NutritionOverview";

/**
 * CartIQ Ask — conversational AI assistant.
 *
 * Floating action button bottom-right of the dashboard. On tap, opens a
 * slide-up drawer with a chat UI. Conversation history is kept in local
 * state (one session per drawer open) and forwarded to the backend so
 * Claude has multi-turn context.
 *
 * Plan-based monthly cap is enforced server-side; the UI displays the
 * remaining budget ("3 of 5 free questions used") and surfaces upgrade
 * prompts on 402 responses.
 */

const SUGGESTION_DEFAULTS = [
  "Why did I overspend this month?",
  "What am I buying most?",
  "Where am I wasting money?",
  "How is my family eating?",
];

const buildSessionId = () =>
  `ask-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// ─── Sub-components ───────────────────────────────────────────────────────────
const TypingDots = () => (
  <div className="flex items-center gap-1 px-3 py-2.5" aria-label="CartIQ is typing">
    {[0, 150, 300].map((d) => (
      <span
        key={d}
        className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]"
        style={{
          animation: "ask-bounce 1s infinite",
          animationDelay: `${d}ms`,
        }}
      />
    ))}
    <style>{`@keyframes ask-bounce { 0%,80%,100%{transform:translateY(0);opacity:0.4} 40%{transform:translateY(-4px);opacity:1} }`}</style>
  </div>
);

const Bubble = ({ role, content }) => {
  if (role === "user") {
    return (
      <div className="flex justify-end" data-testid="ask-bubble-user">
        <div
          className="max-w-[82%] px-3.5 py-2.5 text-sm leading-snug whitespace-pre-wrap"
          style={{
            background: "#FF6B00",
            color: "#0A0A0F",
            borderRadius: "14px 14px 2px 14px",
          }}
        >
          {content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start" data-testid="ask-bubble-assistant">
      <div
        className="max-w-[88%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap text-[#F0EDE8]"
        style={{
          background: "#14141A",
          borderLeft: "3px solid #FF6B00",
          borderRadius: "0 12px 12px 12px",
        }}
      >
        {content}
      </div>
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────
export default function CartIQAsk() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: 'user'|'assistant', content}
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [usage, setUsage] = useState(null); // {plan, month_used, month_limit, suggested_questions, has_report}
  const [error, setError] = useState(null);
  const [needsReport, setNeedsReport] = useState(false);
  const sessionIdRef = useRef(buildSessionId());
  const scrollRef = useRef(null);

  // Lazy-load usage when the drawer first opens so we don't spam the API on
  // every dashboard mount. Closing + reopening refreshes usage too.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const u = await fetchAskUsage();
        if (!cancelled) {
          setUsage(u);
          setNeedsReport(!u.has_report);
        }
      } catch (e) {
        // 401 = not logged in; the FAB shouldn't render anyway, but be defensive.
        if (!cancelled) setError("Couldn't load usage. Try reopening the drawer.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // P1-19 · Proactive "Macro Shift" welcome. Fires once per week per user
  // (backend debounces via macro_welcome_last_at). If the diff has a story
  // to tell, we prepend a synthetic assistant turn with the NutritionOverview
  // card so the user opens the drawer straight into an actionable insight.
  const welcomeFetchedRef = useRef(false);
  useEffect(() => {
    if (!open || welcomeFetchedRef.current) return;
    if (messages.length > 0) return; // don't overwrite an in-flight thread
    welcomeFetchedRef.current = true;
    (async () => {
      try {
        const w = await fetchMacroShiftWelcome();
        if (w?.show && w.message) {
          setMessages([{ role: "assistant", content: w.message, card: w.card || null }]);
        }
      } catch {
        // Silent — welcome is best-effort. No toast, no error.
      }
    })();
  }, [open, messages.length]);

  // Auto-scroll to latest message when content changes.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, thinking]);

  const isAtLimit =
    usage && usage.month_limit !== null && usage.month_used >= usage.month_limit;

  const send = async (questionText) => {
    const q = (questionText || "").trim();
    if (!q || thinking) return;
    if (isAtLimit) return;
    tapFeedback();
    setError(null);

    const userMsg = { role: "user", content: q };
    // Snapshot conversation BEFORE adding the new turn so the backend
    // doesn't see the same turn twice.
    const history = messages.slice();
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setThinking(true);

    try {
      const res = await askCartIQ({
        question: q,
        conversation_history: history,
        session_id: sessionIdRef.current,
      });
      setMessages((m) => [
        ...m,
        // Attach the optional inline card (currently only 'nutrition_overview')
        // as a sibling on the assistant turn so the render layer can decide
        // whether to show it below the text bubble.
        { role: "assistant", content: res.answer, card: res.card || null },
      ]);
      setUsage((u) =>
        u
          ? {
              ...u,
              month_used: res.month_used ?? u.month_used,
              month_limit: res.month_limit ?? u.month_limit,
            }
          : u
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg = "CartIQ couldn't think clearly just now. Try again in a moment.";
      if (status === 402 && detail?.error === "ask_limit_reached") {
        msg = detail.message || "You've used all your CartIQ Ask questions for this month.";
        setUsage((u) =>
          u
            ? {
                ...u,
                month_used: detail.used ?? u.month_used,
                month_limit: detail.limit ?? u.month_limit,
              }
            : u
        );
      } else if (status === 429 && detail?.error === "ask_rate_limited") {
        msg = detail.message || "Too many questions in a short window. Pause for a few minutes.";
      } else if (status === 400 && detail?.error === "no_report") {
        msg = detail.message || "Upload an invoice first so CartIQ has data to learn from.";
        setNeedsReport(true);
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (detail?.message) {
        msg = detail.message;
      }
      // Surface as an assistant turn so the user can see what went wrong
      // inline instead of a toast that fades.
      setMessages((m) => [...m, { role: "assistant", content: `⚠ ${msg}` }]);
    } finally {
      setThinking(false);
    }
  };

  const closeDrawer = () => {
    setOpen(false);
  };

  const reset = () => {
    setMessages([]);
    sessionIdRef.current = buildSessionId();
  };

  // Suggestions are personalised after usage loads; fall back to defaults.
  const suggestions =
    (usage?.suggested_questions && usage.suggested_questions.length === 4
      ? usage.suggested_questions
      : SUGGESTION_DEFAULTS) || SUGGESTION_DEFAULTS;

  return (
    <>
      {/* Floating action button — sits above the bottom nav */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ask CartIQ"
        className="fixed right-4 z-40 flex items-center gap-2 pl-3.5 pr-4 py-3 rounded-full shadow-xl transition-transform active:scale-95"
        style={{
          background: "#FF6B00",
          color: "#0A0A0F",
          // Position above the bottom nav AND above the home-tab "weekly
          // list" FAB so the two never collide. 144px clears both.
          bottom: "calc(144px + env(safe-area-inset-bottom))",
          boxShadow: "0 6px 24px rgba(255,107,0,0.45)",
        }}
        data-testid="ask-fab"
      >
        <span className="relative flex items-center justify-center w-7 h-7 bg-[#0A0A0F]/10 rounded-full">
          <MessageSquare className="w-4 h-4" strokeWidth={2.4} />
          <Sparkles
            className="w-3 h-3 absolute -top-0.5 -right-0.5 text-[#0A0A0F]"
            strokeWidth={2.6}
          />
        </span>
        <span className="text-[12px] uppercase tracking-[0.18em] font-bold whitespace-nowrap">
          Ask CartIQ
        </span>
      </button>

      {/* Slide-up drawer */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col justify-end"
          data-testid="ask-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Ask CartIQ"
        >
          {/* Scrim */}
          <button
            type="button"
            aria-label="Close"
            onClick={closeDrawer}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            data-testid="ask-scrim"
          />

          {/* Drawer body */}
          <div
            className="relative bg-[#0A0A0F] text-[#F0EDE8] border-t border-[#2A2A35] flex flex-col"
            style={{
              height: "70vh",
              maxHeight: "640px",
              borderRadius: "16px 16px 0 0",
            }}
          >
            {/* Header */}
            <header className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A35]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="relative flex items-center justify-center w-7 h-7 bg-[#FF6B00]/15 border border-[#FF6B00]/40 rounded-full">
                  <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
                </span>
                <div className="min-w-0">
                  <div className="font-display uppercase text-base tracking-tight leading-tight">
                    Ask CartIQ
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#777]">
                    Ask anything about your food spending
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button
                    type="button"
                    onClick={reset}
                    className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 text-[#888] hover:text-[#F0EDE8]"
                    data-testid="ask-reset-btn"
                  >
                    New chat
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeDrawer}
                  aria-label="Close"
                  className="p-2 text-[#888] hover:text-[#F0EDE8]"
                  data-testid="ask-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </header>

            {/* Usage strip */}
            {usage && (
              <div
                className="px-4 py-2 text-[10px] uppercase tracking-[0.18em] flex items-center justify-between border-b border-[#1A1A22]"
                style={{ background: "#101018" }}
                data-testid="ask-usage-strip"
              >
                <span className="text-[#888]">
                  {usage.month_limit === null
                    ? `${usage.plan.toUpperCase()} · Unlimited questions`
                    : `${usage.month_used} of ${usage.month_limit} ${usage.plan} questions used`}
                </span>
                {isAtLimit && (
                  <button
                    type="button"
                    onClick={() => navigate("/pricing")}
                    className="text-[#FF6B00] font-bold hover:underline inline-flex items-center gap-1"
                    data-testid="ask-upgrade-link"
                  >
                    Upgrade <ArrowUpRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              data-testid="ask-messages"
            >
              {needsReport && messages.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="w-10 h-10 mx-auto text-[#FF6B00]/60 mb-3" />
                  <div className="font-display uppercase text-lg tracking-tight">
                    No data yet.
                  </div>
                  <p className="text-sm text-[#888] mt-2 max-w-xs mx-auto">
                    Upload your first invoice and CartIQ will know your food
                    life well enough to answer.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      closeDrawer();
                      navigate("/upload");
                    }}
                    className="btn-saffron mt-5 px-5 py-2.5"
                    data-testid="ask-upload-cta"
                  >
                    Upload an invoice
                  </button>
                </div>
              ) : messages.length === 0 ? (
                <div>
                  <div className="text-center pt-4 pb-6">
                    <Sparkles className="w-9 h-9 mx-auto text-[#FF6B00]/60 mb-2" />
                    <div className="font-display uppercase text-base tracking-tight">
                      Hello — ask me anything.
                    </div>
                    <p className="text-[12px] text-[#888] mt-1.5 max-w-xs mx-auto">
                      I read your actual orders, not averages. Pick a starter
                      question below or type your own.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => send(s)}
                        className="text-left text-[12px] leading-snug px-3 py-2.5 border border-[#2A2A35] hover:border-[#FF6B00] hover:bg-[#14141A] transition-colors"
                        data-testid={`ask-suggestion-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((m, i) => (
                    <React.Fragment key={i}>
                      <Bubble role={m.role} content={m.content} />
                      {m.role === "assistant" &&
                        m.card &&
                        m.card.type === "nutrition_overview" && (
                          <NutritionOverview data={m.card.data} />
                        )}
                    </React.Fragment>
                  ))}
                  {thinking && (
                    <div className="flex justify-start" data-testid="ask-typing">
                      <div
                        className="px-1 py-0.5"
                        style={{
                          background: "#14141A",
                          borderLeft: "3px solid #FF6B00",
                          borderRadius: "0 12px 12px 12px",
                        }}
                      >
                        <TypingDots />
                      </div>
                    </div>
                  )}
                </>
              )}

              {error && messages.length === 0 && (
                <div className="text-center text-xs text-red-400 mt-4">{error}</div>
              )}
            </div>

            {/* Input bar */}
            <form
              className="border-t border-[#2A2A35] p-3 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              data-testid="ask-form"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  isAtLimit
                    ? "Limit reached — upgrade to keep asking"
                    : "Ask about your spending..."
                }
                disabled={thinking || isAtLimit || needsReport}
                maxLength={600}
                className="cartiq-input flex-1 text-sm"
                data-testid="ask-input"
              />
              <button
                type="submit"
                disabled={thinking || !input.trim() || isAtLimit || needsReport}
                className="btn-saffron flex items-center justify-center w-11 h-11 p-0 disabled:opacity-50"
                aria-label="Send"
                data-testid="ask-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
